import { useMutation } from '@tanstack/react-query';
import { messagingService } from '../services/messagingService';
import type { Appointment, InventoryItem } from '@clinic-os/shared/types';
import { toast } from 'sonner';

type MessageChannel = 'sms' | 'whatsapp';

interface SendReminderParams {
  appointment: Appointment;
  channel: MessageChannel;
}

interface SendInvoiceParams {
  invoiceId: string;
  patientId: string;
  paymentLink: string;
  channel: MessageChannel;
}

interface SendLowStockParams {
  item: InventoryItem;
  clinicPhone: string;
}

export function useSendAppointmentReminder() {
  return useMutation({
    mutationFn: async ({ appointment, channel }: SendReminderParams) => {
      const patientPhone = messagingService.getPatientPhone(appointment.patientId || '');
      if (!patientPhone) {
        throw new Error('Patient phone number not found');
      }
      const patientName = messagingService.getPatientName(appointment.patientId || '');
      return messagingService.sendAppointmentReminder(
        appointment,
        patientPhone,
        patientName,
        channel
      );
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Reminder sent successfully!', {
          description: result.messageId ? `Message ID: ${result.messageId}` : undefined,
        });
      } else {
        toast.error('Failed to send reminder', {
          description: result.error,
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to send reminder', {
        description: error.message,
      });
    },
  });
}

export function useSendAppointmentConfirmation() {
  return useMutation({
    mutationFn: async ({ appointment, channel }: SendReminderParams) => {
      const patientPhone = messagingService.getPatientPhone(appointment.patientId || '');
      if (!patientPhone) {
        throw new Error('Patient phone number not found');
      }
      const patientName = messagingService.getPatientName(appointment.patientId || '');
      return messagingService.sendAppointmentConfirmation(
        appointment,
        patientPhone,
        patientName,
        channel
      );
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Confirmation sent successfully!', {
          description: result.messageId ? `Message ID: ${result.messageId}` : undefined,
        });
      } else {
        toast.error('Failed to send confirmation', {
          description: result.error,
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to send confirmation', {
        description: error.message,
      });
    },
  });
}

export function useSendInvoice() {
  return useMutation({
    mutationFn: async ({ invoiceId, patientId, paymentLink, channel }: SendInvoiceParams) => {
      const patientPhone = messagingService.getPatientPhone(patientId);
      if (!patientPhone) {
        throw new Error('Patient phone number not found');
      }
      return messagingService.sendInvoice(invoiceId, patientPhone, paymentLink, channel);
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Invoice sent successfully!', {
          description: result.messageId ? `Message ID: ${result.messageId}` : undefined,
        });
      } else {
        toast.error('Failed to send invoice', {
          description: result.error,
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to send invoice', {
        description: error.message,
      });
    },
  });
}

export function useSendLowStockAlert() {
  return useMutation({
    mutationFn: async ({ item, clinicPhone }: SendLowStockParams) => {
      return messagingService.sendLowStockAlert(item, clinicPhone);
    },
    onSuccess: (result) => {
      if (result.success) {
        toast.success('Low stock alert sent!', {
          description: result.messageId ? `Message ID: ${result.messageId}` : undefined,
        });
      } else {
        toast.error('Failed to send low stock alert', {
          description: result.error,
        });
      }
    },
    onError: (error: Error) => {
      toast.error('Failed to send low stock alert', {
        description: error.message,
      });
    },
  });
}

export function useMessagingConfigured() {
  return messagingService.isConfigured();
}
